// Общие части экранов входа, настройки и безопасности: рамка, QR для 2FA, коды восстановления.

import QRCode from 'qrcode'
import { useEffect, useState, type ReactNode } from 'react'

import { BrandMark } from '@/components/layout/BrandMark'
import { Button, Input, type InputProps } from '@/components/ui'
import { cn } from '@/lib/cn'

/** Экран без оболочки: знак, заголовок, карточка по центру. */
export function AuthLayout({
  title,
  description,
  children,
  wide,
}: {
  title: string
  description?: ReactNode
  children: ReactNode
  wide?: boolean
}) {
  return (
    <div className="flex min-h-dvh flex-col items-center bg-bg px-4 pb-10 pt-[calc(2.5rem+env(safe-area-inset-top))] sm:justify-center sm:pt-10">
      <div className={cn('w-full', wide ? 'max-w-lg' : 'max-w-sm')}>
        <div className="mb-6 flex items-center gap-2.5">
          <BrandMark size={32} />
          <span className="text-base font-semibold tracking-tight text-fg">Telos</span>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-fg">{title}</h1>
        {description ? <p className="mt-1.5 text-sm text-muted">{description}</p> : null}
        <div className="mt-6">{children}</div>
      </div>
    </div>
  )
}

/** Поле 6-значного кода из приложения-аутентификатора. */
export function TotpCodeInput(props: Omit<InputProps, 'inputMode' | 'autoComplete'>) {
  return (
    <Input
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9 ]*"
      maxLength={7}
      placeholder="000000"
      className="text-center font-mono text-xl tracking-[0.3em] lg:text-xl"
      {...props}
    />
  )
}

/** QR-код ссылки otpauth:// и ключ для ручного ввода. */
export function TotpQr({ uri, secret }: { uri: string; secret: string }) {
  const [svg, setSvg] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    let alive = true
    QRCode.toString(uri, { type: 'svg', margin: 1, errorCorrectionLevel: 'M' })
      .then((text) => {
        if (alive) setSvg(text)
      })
      .catch(() => {
        if (alive) setSvg(null)
      })
    return () => {
      alive = false
    }
  }, [uri])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret.replace(/\s/g, ''))
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
      <div className="shrink-0 rounded-xl bg-white p-2 shadow-sm ring-1 ring-border">
        {svg ? (
          <img
            src={`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`}
            width={176}
            height={176}
            alt="QR-код для приложения-аутентификатора"
          />
        ) : (
          <div className="h-44 w-44" />
        )}
      </div>
      <div className="min-w-0 flex-1 text-sm">
        <p className="text-muted">Не получается отсканировать — введи ключ вручную:</p>
        <p className="mt-2 break-all font-mono text-[15px] font-medium tracking-wide text-fg">{secret}</p>
        <Button size="sm" icon="copy" className="mt-2" onClick={() => void copy()}>
          {copied ? 'Скопировано' : 'Скопировать ключ'}
        </Button>
      </div>
    </div>
  )
}

/** Коды восстановления: показываются один раз, можно скопировать или скачать файлом. */
export function RecoveryCodesList({ codes }: { codes: string[] }) {
  const [copied, setCopied] = useState(false)
  const text = codes.join('\n')

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  const download = () => {
    const blob = new Blob([`Telos — коды восстановления\nКаждый код работает один раз.\n\n${text}\n`], {
      type: 'text/plain;charset=utf-8',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'health-recovery-codes.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      <ol className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-xl border border-border bg-surface-2 p-4 font-mono text-[15px] text-fg">
        {codes.map((code, index) => (
          <li key={code} className="flex gap-2 tabular-nums">
            <span className="w-5 text-right text-muted">{index + 1}.</span>
            <span>{code}</span>
          </li>
        ))}
      </ol>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" icon="copy" onClick={() => void copy()}>
          {copied ? 'Скопировано' : 'Скопировать'}
        </Button>
        <Button size="sm" icon="download" onClick={download}>
          Скачать .txt
        </Button>
      </div>
    </div>
  )
}
